import { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useApp } from "../hooks/useApp";
import { useAuth } from "../context/AuthProvider";
import { logout } from "../services/authenticationService";

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  isDesktop: boolean;
};

const navItems = [
  {
    id: "onboarding",
    labelKey: "sidebar.create",
    path: "/onboarding/landing",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M15 4V2" />
        <path d="M15 16v-2" />
        <path d="M8 9h2" />
        <path d="M20 9h2" />
        <path d="M17.8 11.8 19 13" />
        <path d="M15 9h.01" />
        <path d="M17.8 6.2 19 5" />
        <path d="m3 21 9-9" />
        <path d="M12.2 6.2 11 5" />
      </svg>
    ),
  },
  {
    id: "explore",
    labelKey: "sidebar.explore",
    path: "/",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 9.75 12 3l9 6.75V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.75z"
        />
      </svg>
    ),
  },
  {
    id: "chats",
    labelKey: "sidebar.chats",
    path: "/chats",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
];

const footerLinks = [
  {
    id: "contact",
    labelKey: "sidebar.contact",
    defaultLabel: "Contact Us",
    path: "/contact",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 10.5a8.38 8.38 0 0 1-1.9.5 3.65 3.65 0 0 0-6.6-2.9 10.32 10.32 0 0 1-7.5 3.8 3.64 3.64 0 0 0 .1 7.3A8.52 8.52 0 0 1 3 21a8.38 8.38 0 0 0 4.2 1.1c6.5 0 10.1-5.3 10.1-9.9 0-.2 0-.4 0-.6a7 7 0 0 0 1.7-1.8Z" />
      </svg>
    ),
  },
  {
    id: "terms",
    labelKey: "sidebar.terms",
    defaultLabel: "Terms & Conditions",
    path: "/legal/terms",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8M8 11h8M8 15h6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3h10l4 4v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      </svg>
    ),
  },
  {
    id: "privacy",
    labelKey: "sidebar.privacy",
    defaultLabel: "Privacy Policy",
    path: "/legal/privacy",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3 5 6v6c0 5 3.8 7.9 7 9 3.2-1.1 7-4 7-9V6l-7-3Z" />
      </svg>
    ),
  },
  {
    id: "report",
    labelKey: "sidebar.report",
    defaultLabel: "Report Content",
    path: "/report",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v18" />
        <path strokeLinecap="round" strokeLinejoin="round" d="m5 4 12 2-3 4 3 4-12-2" />
      </svg>
    ),
  },
  {
    id: "complaintPolicy",
    labelKey: "sidebar.complaintPolicy",
    defaultLabel: "Complaints Policy",
    path: "/legal/complaint",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8M8 11h8M8 15h6" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3h10l4 4v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      </svg>
    ),
  },
  {
    id: "contentRemovalPolicy",
    labelKey: "sidebar.contentRemovalPolicy",
    defaultLabel: "Content Removal Policy",
    path: "/legal/content-removal",
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3 5 6v6c0 5 3.8 7.9 7 9 3.2-1.1 7-4 7-9V6l-7-3Z" />
      </svg>
    ),
  },
];

export default function Sidebar({ isOpen, onClose, isDesktop }: SidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useApp();
  const { user } = useAuth();
  const [isDesktopHovered, setIsDesktopHovered] = useState(false);
  const isAnonymousUser = !user || user.isAnonymous;
  const activeChatId = location.pathname.startsWith("/chats/")
    ? location.pathname.split("/")[2]
    : null;
  const hasUnread = state.chats.some(
    (chat) => chat.hasUnread && chat.id !== activeChatId
  );

  const showMobileMenu = isOpen && !isDesktop;
  const showDesktopMenu = isDesktop && (isOpen || isDesktopHovered);

  const handleNavClick = (path: string) => {
    navigate(path);
    if (showMobileMenu || (isDesktop && isOpen)) {
      onClose();
    }
  };

  const handleDesktopMouseEnter = () => {
    if (isDesktop) {
      setIsDesktopHovered(true);
    }
  };

  const handleDesktopMouseLeave = () => {
    if (isDesktop) {
      setIsDesktopHovered(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.assign("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/" || location.pathname === "/explore";
    }
    return location.pathname.startsWith(path);
  };

  // Don't render anything if closed on mobile
  if (!showMobileMenu && !isDesktop) {
    return null;
  }

  return (
    <>
      {/* Mobile backdrop */}
      {showMobileMenu && (
        <div
          className="fixed inset-0 bg-black/60 z-40"
          onClick={onClose}
        />
      )}

      {/* Icon-only rail (desktop default) */}
      {isDesktop && (
        <div
          className="relative w-[4.5rem] flex-shrink-0"
          onMouseEnter={handleDesktopMouseEnter}
          onMouseLeave={handleDesktopMouseLeave}
        >
          <aside className="bg-[#0a0a0a] border-r border-[#1a1a1a] min-h-[calc(100vh-57px)]">
            <nav className="flex flex-col items-center py-3 gap-3 h-full">
              {navItems.map((item) => {
                const isCreate = item.id === "onboarding";
                const label = t(item.labelKey);
                const showUnread = item.id === "chats" && hasUnread;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleNavClick(item.path)}
                    aria-label={label}
                    title={label}
                    className={`w-11 flex flex-col items-center justify-center gap-1 rounded-xl py-2 transition-colors transition-transform ${
                      isCreate
                        ? "bg-gradient-to-r from-brand-start to-brand-end text-white shadow-[0_4px_0_var(--color-brand-pressed)] hover:translate-y-[1px] hover:shadow-[0_3px_0_var(--color-brand-pressed)] active:translate-y-[3px] active:shadow-[0_1px_0_var(--color-brand-pressed)]"
                        : isActive(item.path)
                          ? "bg-[#1a1a1a] text-white"
                          : "text-gray-400 hover:bg-[#1a1a1a] hover:text-white"
                    }`}
                  >
                    <span className="relative">
                      {item.icon}
                      {showUnread && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-gold rounded-full border-2 border-[#0a0a0a]" />
                      )}
                    </span>
                    <span className="text-[9px] font-medium leading-none tracking-wide">{label}</span>
                  </button>
                );
              })}
              <div className="w-8 h-px bg-[#1a1a1a] my-2" />
              <button
                type="button"
                onClick={() => handleNavClick("/settings")}
                aria-label={t("sidebar.settings")}
                title={t("sidebar.settings")}
                className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${
                  isActive("/settings")
                    ? "bg-[#1a1a1a] text-white"
                    : "text-gray-400 hover:bg-[#1a1a1a] hover:text-white"
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              {isAnonymousUser ? (
                <button
                  type="button"
                  onClick={() => handleNavClick("/auth/signin")}
                  aria-label={t("sidebar.signIn")}
                  title={t("sidebar.signIn")}
                  className="w-11 h-11 flex items-center justify-center rounded-xl text-gray-400 hover:bg-[#1a1a1a] hover:text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h9" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15 9 3 3-3 3" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleLogout}
                  aria-label={t("sidebar.logout")}
                  title={t("sidebar.logout")}
                  className="w-11 h-11 flex items-center justify-center rounded-xl text-red-400 hover:bg-[#1a1a1a] transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H9" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9l-3 3 3 3" />
                  </svg>
                </button>
              )}
              <div className="mt-auto flex flex-col items-center gap-2 pb-3">
                {footerLinks.map((link) => {
                  const label = t(link.labelKey, { defaultValue: link.defaultLabel });
                  return (
                    <button
                      key={link.id}
                      type="button"
                      onClick={() => handleNavClick(link.path)}
                      aria-label={label}
                      title={label}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-[#1a1a1a] transition-colors"
                    >
                      {link.icon}
                    </button>
                  );
                })}
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* Full menu panel */}
      {(isDesktop || showMobileMenu) && (
        <aside
          onMouseEnter={handleDesktopMouseEnter}
          onMouseLeave={handleDesktopMouseLeave}
          className={`bg-[#0a0a0a] border-r border-[#1a1a1a] z-50 fixed top-[57px] left-0 h-[calc(100vh-57px)] w-64 transition-[opacity,transform] duration-200 ease-out ${
            isDesktop
              ? showDesktopMenu
                ? "opacity-100 translate-x-0 pointer-events-auto"
                : "opacity-0 -translate-x-2 pointer-events-none"
              : ""
          }`}
        >
          <nav className="flex flex-col p-3 gap-1 h-full">
            {navItems.map((item) => {
              const isCreate = item.id === "onboarding";
              const label = t(item.labelKey);
              const showUnread = item.id === "chats" && hasUnread;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavClick(item.path)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors transition-transform ${
                    isCreate
                      ? "bg-gradient-to-r from-brand-start to-brand-end text-white shadow-[0_4px_0_var(--color-brand-pressed)] hover:translate-y-[1px] hover:shadow-[0_3px_0_var(--color-brand-pressed)] active:translate-y-[3px] active:shadow-[0_1px_0_var(--color-brand-pressed)]"
                      : isActive(item.path)
                        ? "bg-[#1a1a1a] text-white"
                        : "text-gray-400 hover:bg-[#1a1a1a] hover:text-white"
                  }`}
                >
                  <span className="relative">
                    {item.icon}
                    {showUnread && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-gold rounded-full border-2 border-[#0a0a0a]" />
                    )}
                  </span>
                  <span className="font-medium">{label}</span>
                </button>
              );
            })}
            <div className="h-px bg-[#1a1a1a] my-2" />
            <button
              type="button"
              onClick={() => handleNavClick("/settings")}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                isActive("/settings")
                  ? "bg-[#1a1a1a] text-white"
                  : "text-gray-400 hover:bg-[#1a1a1a] hover:text-white"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="font-medium">{t("sidebar.settings")}</span>
            </button>
            {isAnonymousUser ? (
              <button
                type="button"
                onClick={() => handleNavClick("/auth/signin")}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-left text-gray-400 hover:bg-[#1a1a1a] hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h9" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="m15 9 3 3-3 3" />
                </svg>
                <span className="font-medium">{t("sidebar.signIn")}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-left text-red-400 hover:bg-[#1a1a1a] transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H9" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9l-3 3 3 3" />
                </svg>
                <span className="font-medium">{t("sidebar.logout")}</span>
              </button>
            )}
            <div className="mt-auto pt-3 border-t border-[#1a1a1a]">
              <div className="flex flex-col gap-2 px-4">
                {footerLinks.map((link) => (
                  <button
                    key={link.id}
                    type="button"
                    onClick={() => handleNavClick(link.path)}
                    className="text-left text-xs text-gray-400 hover:text-white transition-colors underline"
                  >
                    {t(link.labelKey, { defaultValue: link.defaultLabel })}
                  </button>
                ))}
              </div>
            </div>
          </nav>
        </aside>
      )}
    </>
  );
}
